import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable';
import { Component } from '@/types';
import {
  KanbanFieldMapping,
  WritebackConfig,
  AutomationRule,
  AutomationCondition,
  AutomationAction,
  AutomationTrigger,
} from '@/types/pm-types';
import {
  isRecord,
  type ComponentDataWriteOperation,
} from '@/types/componentDataBinding';
import { useDataWriteback } from '@/hooks/useDataWriteback';
import { getDataBindingWriteTarget } from './dataBindingWriteTarget';
import { useAutomationRules } from '@/hooks/useAutomationRules';
import { useAuthStore } from '@/stores/auth/auth-store';
import { KanbanColumn } from './KanbanColumn';
import { KanbanCard, getCardId } from './KanbanCard';
import { KanbanCardDetailModal } from './KanbanCardDetailModal';
import { pmTheme } from './pmTheme';

// ---------- Types ----------

interface ColumnDef {
  id: string;
  label: string;
  color?: string;
  wipLimit?: number;
}

export interface KanbanBoardProps {
  component: Component;
  data: Record<string, unknown>[];
  isViewerMode: boolean;
  writeTargetPermissions?: Partial<Record<ComponentDataWriteOperation, boolean>>;
  writeTargetRequestingOrganizationId?: string | null;
  onAddItem?: () => void;
  onEditItem?: (itemId: string, itemData: Record<string, unknown>) => void;
}

// ---------- Defaults ----------

export const DEFAULT_COLUMNS: ColumnDef[] = [
  { id: 'todo', label: 'To Do', color: pmTheme.textMuted },
  { id: 'in-progress', label: 'In Progress', color: '#3B82F6' },
  { id: 'done', label: 'Done', color: '#10B981' },
];

export const DEFAULT_FIELD_MAPPING: KanbanFieldMapping = {
  title: 'title',
  column: 'status',
  id: 'id',
  priority: 'priority',
  assignee: 'assignee',
  sortOrder: 'sortOrder',
};

export const DEFAULT_SAMPLE_DATA: Record<string, unknown>[] = [
  { id: '1', title: 'Design homepage layout', status: 'todo', priority: 'high', assignee: 'Alice', sortOrder: 1 },
  { id: '2', title: 'Set up CI/CD pipeline', status: 'todo', priority: 'medium', assignee: 'Bob', sortOrder: 2 },
  { id: '3', title: 'Implement auth flow', status: 'in-progress', priority: 'high', assignee: 'Charlie', sortOrder: 1 },
  { id: '4', title: 'Write API documentation', status: 'in-progress', priority: 'low', assignee: 'Alice', sortOrder: 2 },
  { id: '5', title: 'Deploy staging environment', status: 'done', priority: 'medium', assignee: 'Bob', sortOrder: 1 },
  { id: '6', title: 'Code review: auth module', status: 'done', priority: 'high', assignee: 'Charlie', sortOrder: 2 },
];

// ---------- Helpers ----------

function groupDataByColumn(
  data: Record<string, unknown>[],
  columns: ColumnDef[],
  fieldMapping: KanbanFieldMapping,
): Record<string, Record<string, unknown>[]> {
  const grouped: Record<string, Record<string, unknown>[]> = {};
  for (const col of columns) {
    grouped[col.id] = [];
  }

  for (const record of data) {
    const colValue = String(record[fieldMapping.column] ?? '');
    if (grouped[colValue]) {
      grouped[colValue].push(record);
    }
    // Records that don't match any column definition are silently dropped
  }

  // Sort within each column by sortOrder if mapped
  if (fieldMapping.sortOrder) {
    const sortField = fieldMapping.sortOrder;
    for (const colId of Object.keys(grouped)) {
      grouped[colId].sort((a, b) => {
        const aVal = Number(a[sortField] ?? 0);
        const bVal = Number(b[sortField] ?? 0);
        return aVal - bVal;
      });
    }
  }

  return grouped;
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function readBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function readStringRecord(value: unknown): Record<string, string> | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const record: Record<string, string> = {};
  for (const [key, entryValue] of Object.entries(value)) {
    if (typeof entryValue !== 'string') {
      return undefined;
    }
    record[key] = entryValue;
  }

  return record;
}

function getComponentProperties(component: Component): Record<string, unknown> {
  return isRecord(component.properties) ? component.properties : {};
}

function parseColumns(value: unknown): ColumnDef[] {
  if (!Array.isArray(value)) {
    return DEFAULT_COLUMNS;
  }

  const columns = value.flatMap((entry) => {
    if (!isRecord(entry)) {
      return [];
    }

    const id = readString(entry.id);
    if (!id) {
      return [];
    }

    return [{
      id,
      label:
        readString(entry.label) ??
        readString(entry.title) ??
        id,
      color: readString(entry.color),
      wipLimit: readNumber(entry.wipLimit),
    }];
  });

  return columns.length > 0 ? columns : DEFAULT_COLUMNS;
}

function parseFieldMapping(value: unknown): KanbanFieldMapping {
  if (!isRecord(value)) {
    return DEFAULT_FIELD_MAPPING;
  }

  const title = readString(value.title);
  const column = readString(value.column);
  const id = readString(value.id);
  const description = readString(value.description);
  const assignee = readString(value.assignee);
  const priority = readString(value.priority);
  const dueDate = readString(value.dueDate);
  const tags = readString(value.tags);
  const sortOrder = readString(value.sortOrder);
  const image = readString(value.image);

  return {
    ...DEFAULT_FIELD_MAPPING,
    ...(title ? { title } : {}),
    ...(column ? { column } : {}),
    ...(id ? { id } : {}),
    ...(description ? { description } : {}),
    ...(assignee ? { assignee } : {}),
    ...(priority ? { priority } : {}),
    ...(dueDate ? { dueDate } : {}),
    ...(tags ? { tags } : {}),
    ...(sortOrder ? { sortOrder } : {}),
    ...(image ? { image } : {}),
  };
}

function parseCardLayout(value: unknown): 'compact' | 'standard' | 'detailed' {
  return value === 'compact' || value === 'standard' || value === 'detailed'
    ? value
    : 'standard';
}

function parseWritebackConfig(value: unknown): WritebackConfig | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const enabled = readBoolean(value.enabled);
  const groupId = readString(value.groupId);
  const schemaId = readString(value.schemaId);
  const idField = readString(value.idField);

  if (
    enabled === undefined ||
    groupId === undefined ||
    schemaId === undefined ||
    idField === undefined
  ) {
    return undefined;
  }

  return {
    enabled,
    groupId,
    schemaId,
    idField,
  };
}

function isAutomationTrigger(value: unknown): value is AutomationTrigger {
  return isRecord(value) &&
    typeof value.type === 'string' &&
    (value.config === undefined || isRecord(value.config));
}

function isAutomationCondition(value: unknown): value is AutomationCondition {
  return isRecord(value) &&
    typeof value.type === 'string' &&
    (value.field === undefined || typeof value.field === 'string') &&
    (value.operator === undefined || typeof value.operator === 'string') &&
    (value.conditions === undefined || (
      Array.isArray(value.conditions) &&
      value.conditions.every(isAutomationCondition)
    ));
}

function isAutomationAction(value: unknown): value is AutomationAction {
  return isRecord(value) &&
    typeof value.type === 'string' &&
    isRecord(value.config);
}

function isAutomationRule(value: unknown): value is AutomationRule {
  return isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.name === 'string' &&
    typeof value.enabled === 'boolean' &&
    isAutomationTrigger(value.trigger) &&
    Array.isArray(value.conditions) &&
    value.conditions.every(isAutomationCondition) &&
    Array.isArray(value.actions) &&
    value.actions.every(isAutomationAction);
}

function parseAutomationRules(value: unknown): AutomationRule[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const rules = value.filter(isAutomationRule);
  return rules.length > 0 ? rules : undefined;
}

function createDefaultKanbanRecord(
  fieldMapping: KanbanFieldMapping,
  columnId: string,
  nextSortOrder: number,
): Record<string, unknown> {
  const record: Record<string, unknown> = {
    [fieldMapping.title]: 'Untitled',
    [fieldMapping.column]: columnId,
  };

  if (fieldMapping.sortOrder !== undefined) {
    record[fieldMapping.sortOrder] = nextSortOrder;
  }

  return record;
}

// ---------- Component ----------

export const KanbanBoard: React.FC<KanbanBoardProps> = ({
  component,
  data,
  isViewerMode,
  writeTargetPermissions,
  writeTargetRequestingOrganizationId,
  onAddItem,
  onEditItem,
}) => {
  const properties = getComponentProperties(component);
  const columns = useMemo(() => parseColumns(properties.columns), [properties.columns]);
  const fieldMapping = useMemo(
    () => parseFieldMapping(properties.fieldMapping),
    [properties.fieldMapping],
  );
  const cardLayout = parseCardLayout(properties.cardLayout);
  const showColumnCounts = readBoolean(properties.showColumnCounts) ?? true;
  const showWipLimits = readBoolean(properties.showWipLimits);
  const showEmptyColumns = readBoolean(properties.showEmptyColumns) ?? true;
  const maxVisibleCards = readNumber(properties.maxVisibleCards);
  const clickAction = readString(properties.clickAction) ?? 'detail-modal';
  const colorMapping = readStringRecord(properties.colorMapping);
  const dragEnabled = isViewerMode && (readBoolean(properties.dragEnabled) ?? true);
  const writeback = useMemo(
    () => parseWritebackConfig(properties.writeback),
    [properties.writeback],
  );
  const automationRules = useMemo(
    () => parseAutomationRules(properties.automationRules),
    [properties.automationRules],
  );
  const title = readString(properties.title);
  const dataBindingWriteTarget = useMemo(
    () => getDataBindingWriteTarget(properties.dataBinding),
    [properties.dataBinding],
  );
  const requiresExplicitWriteTarget =
    dataBindingWriteTarget.requireExplicitWriteTarget;

  // Data writeback
  const { createRecord, updateRecord, writeTargetResolution } = useDataWriteback({
    writebackConfig: writeback,
    writeTarget: dataBindingWriteTarget.writeTarget,
    requireExplicitWriteTarget:
      dataBindingWriteTarget.requireExplicitWriteTarget,
    writePermissions: writeTargetPermissions,
    writeTargetRequestingOrganizationId,
    componentId: component.id,
  });

  // Automation rules
  const user = useAuthStore((s) => s.user);
  const { handleDataEvent, detectNewRecords } = useAutomationRules({
    rules: automationRules,
    writebackConfig: writeback,
    componentId: component.id,
    fieldMapping,
    currentUser: user?.firstName ?? user?.username,
    enabled: isViewerMode,
  });

  // Group data into columns
  const initialGrouped = useMemo(
    () => groupDataByColumn(data, columns, fieldMapping),
    [data, columns, fieldMapping],
  );

  const [columnData, setColumnData] = useState<Record<string, Record<string, unknown>[]>>(initialGrouped);

  // Re-sync when data prop changes + detect new records for automation rules
  useEffect(() => {
    setColumnData(groupDataByColumn(data, columns, fieldMapping));
    if (isViewerMode) detectNewRecords(data);
  }, [data, columns, fieldMapping, isViewerMode, detectNewRecords]);

  // Drag state
  const [activeRecord, setActiveRecord] = useState<Record<string, unknown> | null>(null);

  // Detail modal state
  const [selectedRecord, setSelectedRecord] = useState<Record<string, unknown> | null>(null);

  // Sensors — empty in editor mode to disable drag
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });
  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  });
  const sensors = useSensors(
    ...(dragEnabled ? [pointerSensor, keyboardSensor] : []),
  );

  // Find which column contains a card by its ID
  const findColumnForCard = useCallback(
    (cardId: string): string | undefined => {
      for (const [colId, cards] of Object.entries(columnData)) {
        if (cards.some((r, i) => getCardId(r, fieldMapping, i) === cardId)) {
          return colId;
        }
      }
      return undefined;
    },
    [columnData, fieldMapping],
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const cardId = String(event.active.id);
      const colId = findColumnForCard(cardId);
      if (colId) {
        const record = columnData[colId]?.find((r, i) => getCardId(r, fieldMapping, i) === cardId);
        if (record) setActiveRecord(record);
      }
    },
    [findColumnForCard, columnData, fieldMapping],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveRecord(null);
      const { active, over } = event;
      if (!over) return;

      const activeCardId = String(active.id);
      const sourceColId = findColumnForCard(activeCardId);
      if (!sourceColId) return;

      // Determine target column
      let targetColId: string;
      const overId = String(over.id);

      // Check if dropped on a column droppable (id starts with "column-")
      if (overId.startsWith('column-')) {
        targetColId = overId.replace('column-', '');
      } else {
        // Dropped on another card — find its column
        const overCol = findColumnForCard(overId);
        targetColId = overCol ?? sourceColId;
      }

      if (sourceColId === targetColId) {
        // Reorder within same column
        const oldCards = columnData[sourceColId];
        const oldIndex = oldCards.findIndex((r, i) => getCardId(r, fieldMapping, i) === activeCardId);
        const overCardIndex = oldCards.findIndex((r, i) => getCardId(r, fieldMapping, i) === overId);
        if (oldIndex !== -1 && overCardIndex !== -1 && oldIndex !== overCardIndex) {
          setColumnData(prev => ({
            ...prev,
            [sourceColId]: arrayMove(prev[sourceColId], oldIndex, overCardIndex),
          }));
        }
        return;
      }

      // Move between columns
      const movedRecord = columnData[sourceColId].find(
        (r, i) => getCardId(r, fieldMapping, i) === activeCardId,
      );
      if (!movedRecord) return;

      const recordId = String(
        movedRecord[fieldMapping.id || '_id'] ?? movedRecord['_id'] ?? movedRecord['id'] ?? '',
      );
      const fieldChanges = { [fieldMapping.column]: targetColId };

      if (requiresExplicitWriteTarget) {
        if (!recordId || writeTargetResolution.status !== 'ready') {
          if (recordId) {
            void updateRecord(recordId, fieldChanges);
          }
          return;
        }
      }

      const prevData = { ...columnData };
      setColumnData(prev => {
        const newSource = prev[sourceColId].filter(
          (r, i) => getCardId(r, fieldMapping, i) !== activeCardId,
        );
        const newTarget = [...(prev[targetColId] ?? []), { ...movedRecord, [fieldMapping.column]: targetColId }];
        return { ...prev, [sourceColId]: newSource, [targetColId]: newTarget };
      });

      // Writeback to data-service
      if (writeTargetResolution.status === 'ready') {
        if (recordId) {
          updateRecord(recordId, fieldChanges, {
            onRollback: () => setColumnData(prevData),
          });
        }
      }

      // Fire automation rules for column move
      handleDataEvent({
        type: 'onColumnMove',
        record: { ...movedRecord, [fieldMapping.column]: targetColId },
        previousRecord: movedRecord,
        fromColumn: sourceColId,
        toColumn: targetColId,
      }).catch(console.error);
    },
    [
      findColumnForCard,
      columnData,
      fieldMapping,
      requiresExplicitWriteTarget,
      writeTargetResolution.status,
      updateRecord,
      handleDataEvent,
    ],
  );

  const hasBoundAddPath = requiresExplicitWriteTarget;
  const hasAddAction = hasBoundAddPath || onAddItem !== undefined;

  const handleAddItemClick = useCallback(() => {
    if (!hasBoundAddPath) {
      onAddItem?.();
      return;
    }

    const firstColumnId = columns[0]?.id;
    if (firstColumnId === undefined) {
      return;
    }

    const record = createDefaultKanbanRecord(
      fieldMapping,
      firstColumnId,
      data.length + 1,
    );
    void createRecord(record);
  }, [
    columns,
    createRecord,
    data.length,
    fieldMapping,
    hasBoundAddPath,
    onAddItem,
  ]);

  const handleCardClick = useCallback(
    (record: Record<string, unknown>) => {
      if (isViewerMode && clickAction === 'detail-modal') {
        setSelectedRecord(record);
      }
    },
    [isViewerMode, clickAction],
  );

  return (
    <div
      data-testid="kanban-board"
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        color: pmTheme.text,
      }}
    >
      {/* Board header with title and add button */}
      {(title || hasAddAction) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 8px 4px',
          }}
        >
          {title && (
            <div
              data-testid="kanban-board-title"
              style={{
                fontWeight: 600,
                fontSize: 15,
                color: pmTheme.text,
              }}
            >
              {title}
            </div>
          )}
          <div style={{ flex: 1 }} />
          {hasAddAction && (
            <button
              onClick={handleAddItemClick}
              data-testid="kanban-add-item-button"
              style={{
                padding: '3px 10px',
                fontSize: 11,
                borderRadius: 4,
                border: `1px solid ${pmTheme.input}`,
                background: pmTheme.surfaceSubtle,
                color: pmTheme.text,
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              + Add Item
            </button>
          )}
        </div>
      )}

      {/* Columns */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            flex: 1,
            overflowX: 'auto',
            padding: 8,
          }}
        >
          {columns
            .filter(col => showEmptyColumns || (columnData[col.id]?.length ?? 0) > 0)
            .map(col => (
            <KanbanColumn
              key={col.id}
              columnDef={col}
              cards={columnData[col.id] ?? []}
              fieldMapping={fieldMapping}
              cardLayout={cardLayout}
              colorMapping={colorMapping}
              showColumnCounts={showColumnCounts}
              showWipLimits={showWipLimits}
              maxVisibleCards={maxVisibleCards}
              isViewerMode={isViewerMode}
              dragEnabled={dragEnabled}
              onCardClick={handleCardClick}
              onEditItem={onEditItem}
            />
          ))}
        </div>

        {/* Drag overlay — shows card clone during drag */}
        <DragOverlay>
          {activeRecord ? (
            <KanbanCard
              record={activeRecord}
              fieldMapping={fieldMapping}
              cardLayout={cardLayout}
              colorMapping={colorMapping}
              isViewerMode={false}
              dragEnabled={false}
              index={0}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Card detail modal */}
      {selectedRecord && (
        <KanbanCardDetailModal
          record={selectedRecord}
          fieldMapping={fieldMapping}
          writeback={writeback}
          writeTarget={dataBindingWriteTarget.writeTarget}
          requireExplicitWriteTarget={
            dataBindingWriteTarget.requireExplicitWriteTarget
          }
          writePermissions={writeTargetPermissions}
          writeTargetRequestingOrganizationId={writeTargetRequestingOrganizationId}
          componentId={component.id}
          onClose={() => setSelectedRecord(null)}
        />
      )}
    </div>
  );
};

KanbanBoard.displayName = 'KanbanBoard';
